import {UpperCasePipe, NgTemplateOutlet} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router, RouterLink, RouterLinkActive} from '@angular/router';
import {MenuItem} from 'primeng/api';
import {Avatar} from 'primeng/avatar';
import {Breadcrumb} from 'primeng/breadcrumb';
import {ButtonDirective} from 'primeng/button';
import {IconField} from 'primeng/iconfield';
import {InputIcon} from 'primeng/inputicon';
import {InputText} from 'primeng/inputtext';
import {Menu} from 'primeng/menu';
import {MessageModule} from 'primeng/message';
import {ScrollTop} from 'primeng/scrolltop';
import {Tooltip} from 'primeng/tooltip';
import {
  Sidebar, SidebarAside, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader,
  SidebarLayout, SidebarMain, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarPanel, SidebarSpacer, SidebarTrigger,
} from 'primeng/sidebar';
import {filter, map} from 'rxjs';
import {SessionData} from '../models/session-data';
import {IdentityService} from '../services/identity.service';
import {VersionCheckService} from '../services/version-check.service';
import {BreadcrumbTrail} from './breadcrumb-trail';
import {breadcrumbFor, buildNavigation, crumbLinkFor, visibleNavigation} from './navigation';

/**
 * Оболочка CP (PAR-12961, макет PAR-12960 `harness/src/app/screens/shell.component.ts`): боковое меню p-sidebar,
 * верхняя полоса с крошкой, область страницы. Страница приходит через ng-content.
 *
 * `chrome` выключает меню и полосу (экран входа), но содержимое остаётся тем же проецируемым узлом: ng-content
 * один, внутри ng-template, и выводится через ngTemplateOutlet в одной из двух веток. Проецируемые узлы принадлежат
 * представлению родителя (AppComponent), поэтому смена ветки переносит router-outlet, а не создаёт его заново.
 */
@Component({
  selector: 'app-shell',
  imports: [
    NgTemplateOutlet, UpperCasePipe, RouterLink, RouterLinkActive,
    Avatar, Breadcrumb, ButtonDirective, IconField, InputIcon, InputText, Menu, MessageModule, ScrollTop, Tooltip,
    Sidebar, SidebarAside, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupContent, SidebarGroupLabel, SidebarHeader,
    SidebarLayout, SidebarMain, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarPanel, SidebarSpacer, SidebarTrigger,
  ],
  templateUrl: './shell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShellComponent {
  private readonly identityService = inject(IdentityService);
  private readonly router = inject(Router);
  private readonly trail = inject(BreadcrumbTrail);
  /** Проверка версии сборки (PAR-12788) идёт, пока показана оболочка; баннер — в шаблоне. */
  protected readonly versionCheck = inject(VersionCheckService);

  /** Показывать меню и верхнюю полосу (пользователь вошёл). */
  readonly chrome = input(false);

  /** Развёрнуто ли меню; не сохраняется между загрузками (решение владельца). */
  protected readonly expanded = signal(true);

  // Роли и почта читаются из сессии: она меняется при каждом входе и выходе, поэтому меню и блок пользователя
  // пересчитываются и при повторном входе под другой ролью.
  private readonly session = toSignal(this.identityService.userSession, {requireSync: true});

  protected readonly navigation = computed(() => {
    this.session();
    return visibleNavigation(buildNavigation({
      admin: this.identityService.isAdmin(),
      manager: this.identityService.isManager(),
      saleManager: this.identityService.isSaleManager(),
    }));
  });

  protected readonly email = computed(() => (this.session() as SessionData | null)?.email ?? '');
  protected readonly initial = computed(() => this.email().charAt(0).toUpperCase());
  /** Подпись роли — старшая из ролей пользователя: Admin > Manager > Sale manager. */
  protected readonly role = computed(() => {
    this.session();
    if (this.identityService.isAdmin()) return 'Admin';
    if (this.identityService.isManager()) return 'Manager';
    if (this.identityService.isSaleManager()) return 'Sale manager';
    return null;
  });

  /**
   * Открыт ли попап меню пользователя (aria-expanded кнопки), по onShow/onHide p-menu. Сбрасывается при смене
   * `chrome`: при выходе попап уничтожается вместе с веткой и onHide может не прийти — после повторного входа кнопка
   * не должна объявлять меню открытым.
   */
  protected readonly userMenuOpen = linkedSignal(() => {
    this.chrome();
    return false;
  });

  protected readonly userMenu: MenuItem[] = [
    {label: 'Log out', icon: 'pi pi-sign-out', command: () => this.identityService.logout()},
  ];

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects),
    ),
    {initialValue: this.router.url},
  );

  protected readonly home: MenuItem = {icon: 'pi pi-home', routerLink: '/'};

  /**
   * Раздел — текст без ссылки; текущая страница помечена `state.current` (в шаблоне — aria-current="page"); уровни
   * глубже пункта меню дописывает страница (`useBreadcrumbTrail`), пункт меню тогда — ссылка.
   * Адрес без крошки (главная) — `undefined`, а не пустой массив: p-breadcrumb рисует разделитель после «домой»,
   * если model задан, даже пустой.
   */
  protected readonly crumbs = computed<MenuItem[] | undefined>(() => {
    const crumb = breadcrumbFor(this.url());
    if (!crumb) return undefined;
    // Уровни страницы глубже пункта меню (BreadcrumbTrail): пункт меню — ссылка назад, текущая — последний уровень.
    const trail = this.trail.items();
    const page: MenuItem = trail.length
      ? {label: crumb.page, routerLink: crumbLinkFor(this.url())}
      : {label: crumb.page, state: {current: true}};
    const deeper = trail.map((item, i) => (i === trail.length - 1 ? {...item, routerLink: undefined, state: {current: true}} : item));
    return [...(crumb.section ? [{label: crumb.section}] : []), page, ...deeper];
  });
}
